import { Caption1, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { Dismiss12Regular } from "@fluentui/react-icons";
import { useRef, useState, type KeyboardEvent } from "react";
import { ConfirmDialog } from "@/components/app-dialog";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { ColumnsVisual, niceTicks, StackedColumnsVisual, TrendLineVisual } from "@/components/visual/charts";
import { ReportVisual } from "@/components/visual/ReportVisual";
import { RowMoreMenu } from "@/features/resources/RowMoreMenu";
import { fmtCurrency, fmtDelta, fmtMM, fmtSpi } from "@/lib/format";
import { formatDate } from "@/lib/date-format";
import { useProjectStore } from "@/store/project-store";
import { attentionTrendStacks, burnAxisMax, burnSeries, chronological, progressSeries, spiAxis, spiSeries, trendAxis, trendTable, varianceColumns } from "./report-charts";
import { reportLevel } from "./report-model";
import { CURRENT_KEY, LevelDot, SourceBadge, useMaskedMoney, useReportPoints, useReportStyles, useTrendRows, type ReportPoint } from "./report-ui";

const useStyles = makeStyles({
  nameCell: { display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 },
  nameRow: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalXS, minWidth: 0 },
  groupTh: { textAlign: "left", color: tokens.colorNeutralForeground3, borderLeft: `1px solid ${tokens.colorNeutralStroke2}` },
  groupStart: { borderLeft: `1px solid ${tokens.colorNeutralStroke2}` },
  row: { transitionProperty: "background-color", transitionDuration: tokens.durationFaster },
  currentRow: { backgroundColor: tokens.colorNeutralBackground2 },
  hoverRow: { backgroundColor: tokens.colorNeutralBackground1Hover },
  selectedRow: { backgroundColor: tokens.colorBrandBackground2, boxShadow: `inset 3px 0 0 ${tokens.colorBrandStroke1}` },
  cellCompact: { paddingTop: tokens.spacingVerticalXS, paddingBottom: tokens.spacingVerticalXS },
  actionCell: { width: "40px", textAlign: "center" },
  tableHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: tokens.spacingHorizontalM, minHeight: "28px" },
  selection: { display: "inline-flex", alignItems: "center", gap: tokens.spacingHorizontalXS, minWidth: 0 },
});

const CHART_HEIGHT = 200;

export function ReportTrendView({ onOpenStatus, onOpenChanges, canManage = false }: { onOpenStatus: (key: string) => void; onOpenChanges: (fromKey: string, toKey: string) => void; canManage?: boolean }) {
  const s = useReportStyles();
  const st = useStyles();
  const ts = useDataTableStyles();
  const masked = useMaskedMoney();
  const points = useReportPoints();
  const rows = useTrendRows(points);
  const [pendingDelete, setPendingDelete] = useState<ReportPoint | null>(null);
  /** 차트 점·막대·표 행으로 고른 기간(교차 강조). hover는 표 행을 가리키는 동안만 */
  const [selected, setSelected] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());

  if (points.periods.length === 0) {
    return (
      <div className={s.wrap}>
        <GhostEmptyState title="아직 쌓인 보고 기간이 없어요" description="마감할 때마다 추이가 쌓입니다." />
      </div>
    );
  }

  const timeline = chronological(rows);
  const axis = trendAxis(timeline);
  const focusKey = hovered ?? selected;
  const highlightIndex = focusKey ? axis.keys.indexOf(focusKey) : -1;
  const pick = (key: string) => {
    setSelected((cur) => (cur === key ? null : key));
    window.requestAnimationFrame(() => rowRefs.current.get(key)?.scrollIntoView({ block: "nearest", behavior: "smooth" }));
  };
  const pickIndex = (i: number) => {
    const key = axis.keys[i];
    if (key) pick(key);
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && selected) setSelected(null);
  };

  const progress = progressSeries(timeline);
  const spi = spiSeries(timeline);
  const spiRange = spiAxis(timeline);
  const burn = burnSeries(timeline);
  const burnMax = burnAxisMax(timeline);
  const attentionStacks = attentionTrendStacks(timeline, { highlightKey: focusKey, onPick: pick });
  const attentionPeak = Math.max(4, ...timeline.map((r) => r.m.execBlocked + r.m.execOverdue + r.m.delayCount));
  const attentionTicks = niceTicks(attentionPeak, 4);
  const curLast = timeline[timeline.length - 1]!.m;
  const selectedPoint = selected ? points.byKey.get(selected) : undefined;
  const lineProps = { labels: axis.labels, calloutTitles: axis.labels, highlightIndex, onPointClick: pickIndex };
  const pointHint = "점을 누르면 아래 표에서 그 기간을 찾아요";

  return (
    <div className={s.wrap} onKeyDown={onKeyDown}>
      <div className={s.grid2}>
        <ReportVisual
          title="진척 추이"
          description={progress.length > 1 ? `실제와 계획(기준선) · ${pointHint}` : `실제 진척 · ${pointHint}`}
          meta={`${timeline.length}개 시점`}
          height={CHART_HEIGHT}
          table={trendTable("progress", timeline, pick)}
          chart={(h) => <TrendLineVisual {...lineProps} series={progress} height={h} yMin={0} yMax={100} yTicks={[0, 25, 50, 75, 100]} formatY={(v) => `${Math.round(v)}%`} ariaLabel={`진척 추이: ${timeline.map((r, i) => `${axis.labels[i]} 실제 ${r.m.progress}%`).join(", ")}`} />}
        />
        <ReportVisual
          title="기준선 대비 종료일 편차"
          description="근무일 · 위로 갈수록 늦음 · 막대를 누르면 표에서 찾아요"
          meta={timeline.some((r) => r.m.hasBaseline) ? `현재 ${fmtDelta(timeline[timeline.length - 1]!.m.finishVarianceDays)}` : "기준선 없음"}
          height={CHART_HEIGHT}
          table={trendTable("variance", timeline, pick)}
          chart={(h) => <ColumnsVisual data={varianceColumns(timeline, { highlightKey: focusKey, onPick: pick })} height={h} showLegend formatY={(v) => `${Math.round(v)}d`} ariaLabel={`종료일 편차: ${timeline.map((r, i) => `${axis.labels[i]} ${r.m.hasBaseline ? fmtDelta(r.m.finishVarianceDays) : "기준선 없음"}`).join(", ")}`} />}
        />
        <ReportVisual
          title="SPI 추이"
          description="1.0 = 계획대로 · 0.9 아래는 위험"
          meta={spi.length ? `현재 ${fmtSpi(timeline[timeline.length - 1]!.m.spi)}` : "기준선 없음"}
          height={CHART_HEIGHT}
          table={spi.length ? trendTable("spi", timeline, pick) : undefined}
          chart={spi.length ? (h) => <TrendLineVisual {...lineProps} series={spi} height={h} yMin={spiRange.min} yMax={spiRange.max} formatY={(v) => v.toFixed(1)} ariaLabel={`SPI 추이: ${timeline.map((r, i) => `${axis.labels[i]} ${fmtSpi(r.m.spi)}`).join(", ")}`} /> : undefined}
        >
          <GhostEmptyState compact title="기준선이 있어야 SPI를 계산해요" description="기준선을 저장하면 SPI가 쌓입니다." />
        </ReportVisual>
        <ReportVisual
          title="예산 대비 계획 비용"
          description="금액을 가려도 비율은 보여요 · 100%를 넘으면 초과"
          meta={burn.length ? `현재 ${burn[0]!.valueLabels?.[timeline.length - 1] ?? "—"}` : "예산 미설정"}
          height={CHART_HEIGHT}
          table={burn.length ? trendTable("burn", timeline, pick) : undefined}
          chart={burn.length ? (h) => <TrendLineVisual {...lineProps} series={burn} height={h} yMin={0} yMax={burnMax} yTicks={niceTicks(burnMax)} formatY={(v) => `${Math.round(v)}%`} ariaLabel={`예산 대비 계획 비용: ${timeline.map((r, i) => `${axis.labels[i]} ${burn[0]!.valueLabels?.[i] ?? "—"}`).join(", ")}`} /> : undefined}
        >
          <GhostEmptyState compact title="예산을 정하면 비율 추이가 보여요" description="예산을 입력하면 비율이 쌓입니다." />
        </ReportVisual>
      </div>

      <ReportVisual
        title="병목 · 지연 추이"
        description="막힌 Task · 마감 초과 Task · 계획 대비 지연된 WBS 작업의 기간별 누적"
        meta={`현재 막힘 ${curLast.execBlocked} · 초과 ${curLast.execOverdue} · 지연 ${curLast.delayCount}`}
        height={180}
        table={trendTable("attention", timeline, pick)}
        chart={(h) => (
          <StackedColumnsVisual
            data={attentionStacks}
            height={h}
            yMax={attentionTicks.at(-1)}
            yTicks={attentionTicks}
            formatY={(v) => `${Math.round(v)}건`}
            ariaLabel={`병목 및 지연 추이: ${timeline.map((r, i) => `${axis.labels[i]} 막힘 ${r.m.execBlocked}건, 초과 ${r.m.execOverdue}건, 지연 ${r.m.delayCount}건`).join(", ")}`}
          />
        )}
      />

      <section className={s.card} aria-label="보고 기간별 지표">
        <div className={st.tableHead}>
          <span className={s.cardTitle}>보고 기간별 지표</span>
          {selectedPoint ? (
            <span className={st.selection}>
              <span className={mergeClasses(s.meta, s.truncate)}>선택: {selectedPoint.label} · Esc로 해제</span>
              <FuiButton density="dialog" appearance="subtle" icon={<Dismiss12Regular />} aria-label="선택 해제" onClick={() => setSelected(null)} />
            </span>
          ) : (
            <span className={s.meta}>행을 가리키면 위 차트에서 같은 기간이 강조돼요</span>
          )}
        </div>
        <div className={ts.frame}>
          <table className={ts.table} aria-label="보고 기간별 지표">
            <thead>
              <tr>
                <th className={ts.th} rowSpan={2} scope="col">
                  보고 기간
                </th>
                <th className={mergeClasses(ts.th, st.groupTh)} colSpan={5} scope="colgroup">
                  진척
                </th>
                <th className={mergeClasses(ts.th, st.groupTh)} colSpan={3} scope="colgroup">
                  일정
                </th>
                <th className={mergeClasses(ts.th, st.groupTh)} colSpan={2} scope="colgroup">
                  자원·비용
                </th>
                <td className={mergeClasses(ts.th, st.actionCell)} aria-hidden />
              </tr>
              <tr>
                <th className={mergeClasses(ts.th, ts.num, st.groupStart)} scope="col">진척 기준일</th>
                <th className={mergeClasses(ts.th, ts.num)} scope="col">진척</th>
                <th className={mergeClasses(ts.th, ts.num)} scope="col">계획</th>
                <th className={mergeClasses(ts.th, ts.num)} scope="col">SPI</th>
                <th className={mergeClasses(ts.th, ts.num)} scope="col">Task 완료</th>
                <th className={mergeClasses(ts.th, ts.num, st.groupStart)} scope="col">예상 종료</th>
                <th className={mergeClasses(ts.th, ts.num)} scope="col">기준선 대비</th>
                <th className={mergeClasses(ts.th, ts.num)} scope="col">지연</th>
                <th className={mergeClasses(ts.th, ts.num, st.groupStart)} scope="col">인력</th>
                <th className={mergeClasses(ts.th, ts.num)} scope="col">계획 비용</th>
                <th className={mergeClasses(ts.th, st.actionCell)} scope="col">
                  <span className="sr-only">동작</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ point, m }, i) => {
                const prev = rows[i + 1];
                const cell = mergeClasses(ts.td, st.cellCompact);
                const num = mergeClasses(cell, ts.num);
                const level = reportLevel(m).level;
                const isSelected = selected === point.key;
                return (
                  <tr
                    key={point.key}
                    ref={(el) => {
                      if (el) rowRefs.current.set(point.key, el);
                      else rowRefs.current.delete(point.key);
                    }}
                    className={mergeClasses(st.row, point.key === CURRENT_KEY && st.currentRow, hovered === point.key && st.hoverRow, isSelected && st.selectedRow)}
                    aria-selected={isSelected || undefined}
                    onMouseEnter={() => setHovered(point.key)}
                    onMouseLeave={() => setHovered(null)}
                    onFocus={() => setHovered(point.key)}
                    onBlur={() => setHovered(null)}
                  >
                    <td className={cell}>
                      <div className={st.nameCell}>
                        <div className={st.nameRow}>
                          <LevelDot level={level} />
                          <span className={s.truncate} title={point.label}>
                            {point.label}
                          </span>
                          <SourceBadge period={point.period} />
                        </div>
                        {point.period?.note && (
                          <Caption1 className={mergeClasses(s.tertiary, s.truncate)} title={point.period.note}>
                            {point.period.note}
                          </Caption1>
                        )}
                      </div>
                    </td>
                    <td className={mergeClasses(num, st.groupStart)}>{formatDate(m.statusDate, "numeric")}</td>
                    <td className={num}>{m.progress}%</td>
                    <td className={mergeClasses(num, s.tertiary)}>{m.plannedProgress !== null ? `${m.plannedProgress}%` : "—"}</td>
                    <td className={mergeClasses(num, m.spi !== null && m.spi < 0.9 ? s.danger : undefined)}>{fmtSpi(m.spi)}</td>
                    <td className={num}>
                      {m.execDone}/{m.execTotal}
                    </td>
                    <td className={mergeClasses(num, st.groupStart)}>{formatDate(m.projectFinish, "numeric")}</td>
                    <td className={mergeClasses(num, m.finishVarianceDays > 0 ? s.danger : undefined)}>{m.hasBaseline ? fmtDelta(m.finishVarianceDays) : "—"}</td>
                    <td className={num}>{m.delayCount}</td>
                    <td className={mergeClasses(num, st.groupStart)}>
                      {m.headcount}명 <span className={s.tertiary}>{fmtMM(m.plannedMM)}</span>
                    </td>
                    <td className={mergeClasses(num, m.burn > 1 ? s.danger : undefined)}>{fmtCurrency(m.costTotal, masked)}</td>
                    <td className={mergeClasses(cell, st.actionCell)}>
                      <RowMoreMenu
                        label={`${point.label} 동작`}
                        items={[
                          { label: "현황 보고 보기", onClick: () => onOpenStatus(point.key) },
                          ...(prev ? [{ label: "직전 기간과 비교", onClick: () => onOpenChanges(prev.point.key, point.key) }] : []),
                          ...(canManage && point.period ? [{ label: "보고 기간 삭제…", onClick: () => setPendingDelete(point) }] : []),
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <ConfirmDialog
        open={!!pendingDelete}
        title="보고 기간 삭제"
        message="이 보고 기간을 삭제할까요? 이 시점의 스냅샷과 마감 지표가 함께 사라지며 되돌릴 수 없습니다."
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete?.period) useProjectStore.getState().deleteReportPeriod(pendingDelete.period.id);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}
